import React, { ButtonHTMLAttributes, memo, useEffect, useMemo } from 'react';
import { CSSTransition } from 'react-transition-group';
import { ChevronArrowDownIcon } from '../../assets/icons/ChevronArrowDownIcon';
import { CloudDownloadIcon } from '../../assets/icons/CloudDownloadIcon';
import { PercentLoaderIcon } from '../../assets/icons/PercentLoaderIcon';
import { getMergedClassNames, getUniqueKey } from '../../utils/HTMLutils';
import './ItemDropdownDownload.scss';
const rootClassName = 'component-item-dropdown-download';

export type DownloadItem = {
  id?: string;
  label: string;
  loading?: boolean;
  onClick: (evt: React.MouseEvent<HTMLButtonElement>) => void;
};

export interface ItemDropdownDownloadProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  opened: boolean;
  setOpened: (val: boolean) => void;
  closeOnOutClick: boolean;
  closeOnEsc: boolean;
  downloads: DownloadItem[];
}

function ItemDropdownDownload(props: ItemDropdownDownloadProps) {
  const id = useMemo(() => (props.id ? props.id : getUniqueKey()), [props.id]);
  const filteredProps = useMemo(() => {
    const p = {
      ...props,
      className: getMergedClassNames([
        rootClassName,
        props.className || '',
        props.opened ? 'opened' : 'closed',
      ]),
      onClick: (event: any) => {
        if (typeof props.onClick === 'function') {
          props.onClick(event);
        }
        if (typeof props.setOpened === 'function') {
          props.setOpened(!props.opened);
        }
      },
    } as any;
    delete p.label;
    delete p.opened;
    delete p.setOpened;
    delete p.closeOnOutClick;
    delete p.closeOnEsc;
    delete p.downloads;
    return p;
  }, [props]);

  // OnOutClick
  useEffect(() => {
    const onHandleClick = (evt: any) => {
      if (evt.path) {
        if (
          document.querySelector(
            `#${id} .${rootClassName}-panel.panel-fade-enter-done`
          ) &&
          !evt.path.includes(document.querySelector(`#${id}`))
        )
          props.setOpened(false);
      } else {
        // FIREFOX
        let aux = evt.target;
        let deveFechar = true;
        while (aux && aux.id !== 'root') {
          if (
            aux &&
            aux.id &&
            aux.id === id &&
            aux.classList.contains(`${rootClassName}-outer`)
          ) {
            deveFechar = false;
            break;
          }
          aux = aux.parentElement;
        }
        if (deveFechar) {
          props.setOpened(false);
        }
      }
    };
    const onEscPress = (evt: any) => {
      if (evt.key === 'Escape') {
        props.setOpened(false);
      }
    };

    if (props.closeOnOutClick) {
      if (props.opened) {
        document.addEventListener('mousedown', onHandleClick);
      } else {
        document.removeEventListener('mousedown', onHandleClick);
      }
    }
    if (props.closeOnEsc) {
      if (props.opened) {
        document.addEventListener('keyup', onEscPress);
      } else {
        document.removeEventListener('keyup', onEscPress);
      }
    }
    return () => {
      document.removeEventListener('mousedown', onHandleClick);
      document.removeEventListener('click', onHandleClick);
      document.removeEventListener('keyup', onEscPress);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.opened]);

  return (
    <div className={`${rootClassName}-outer`} id={id}>
      <button {...filteredProps}>
        <div className={`${rootClassName}-label`}>{props.label}</div>
        <div
          className={getMergedClassNames([
            `${rootClassName}-arrow`,
            props.opened ? 'up' : 'down',
          ])}
        >
          <ChevronArrowDownIcon />
        </div>
      </button>
      <CSSTransition
        in={props.opened}
        timeout={300}
        classNames='panel-fade'
        unmountOnExit
      >
        <div className={`${rootClassName}-panel`}>
          {props.downloads && props.downloads.length
            ? props.downloads.map((download, index) => {
                return (
                  <button
                    id={download.id || undefined}
                    className={`${rootClassName}-item ${
                      download.loading ? 'loading' : ''
                    }`}
                    onClick={(evt) => {
                      if (
                        !download.loading &&
                        typeof download.onClick === 'function'
                      ) {
                        download.onClick(evt);
                      }
                    }}
                    key={index}
                  >
                    <div className={`${rootClassName}-item-label`}>
                      {download.label}
                    </div>
                    <div className={`${rootClassName}-download-icon`}>
                      {download.loading ? (
                        <PercentLoaderIcon indefinido />
                      ) : (
                        <CloudDownloadIcon />
                      )}
                    </div>
                  </button>
                );
              })
            : undefined}
        </div>
      </CSSTransition>
    </div>
  );
}

ItemDropdownDownload.defaultProps = {
  closeOnOutClick: true,
  closeOnEsc: true,
};

export default memo(ItemDropdownDownload);
